import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { message } from 'antd';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import StatusSelect, { tintFor } from '../../Components/StatusSelect';
import Spinner from '../../Components/Spinner';
import Hint from '../../Components/Hint';
import useHints from '../../Components/useHints';
import CustomsTags from '../Traders/Components/CustomsTags';
import { useCustomsEnabled } from '../Traders/customsTreatments';
import './SavedTradersPage.css';

const STATUS_BY_SLUG = {
    prospects: 'Prospect',
    clients: 'Client',
    lapsed: 'Lapsed Client',
    rejected: 'Rejected',
};

const FACETS = [
    { slug: null, label: 'All', dot: 'status-dot-unsaved', match: '/saved-traders$' },
    { slug: 'prospects', label: 'Prospects', dot: 'status-dot-prospect' },
    { slug: 'clients', label: 'Clients', dot: 'status-dot-client' },
    { slug: 'lapsed', label: 'Lapsed', dot: 'status-dot-lapsed' },
    { slug: 'rejected', label: 'Rejected', dot: 'status-dot-rejected' },
];

const PAGE_COPY = {
    Prospect: {
        title: 'Prospects.',
        subtitle: 'Active opportunities — accounts worth a call this week.',
    },
    Client: {
        title: 'Clients.',
        subtitle: 'Live accounts. Keep an eye on volume and lane shifts.',
    },
    'Lapsed Client': {
        title: 'Lapsed clients.',
        subtitle: 'Quiet accounts. Worth a re-engagement nudge.',
    },
    Rejected: {
        title: 'Rejected.',
        subtitle: 'Closed out — kept here in case anything changes.',
    },
    null: {
        title: 'Pipeline.',
        subtitle: 'Every saved trader across all stages, in one view.',
    },
};

const facetPath = (slug) => (slug ? `/saved-traders/${slug}` : '/saved-traders');

const fmtAddress = (t) =>
    [t.address_1, t.address_2, t.post_code].filter(Boolean).join(', ') || 'No address';

const fmtUser = (t) =>
    `${t.first_name || ''} ${t.last_name || ''}`.trim() || 'Unknown user';

const SavedTradersPage = () => {
    const customsOn = useCustomsEnabled(); // org setting — no "Might need" column when off
    const navigate = useNavigate();
    const location = useLocation();

    const [savedTraders, setSavedTraders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [userFilter, setUserFilter] = useState('all');
    const [downloading, setDownloading] = useState(false);
    const hints = useHints();

    const currentSlug = useMemo(() => {
        const segments = location.pathname.split('/').filter(Boolean);
        const tail = segments[segments.length - 1];
        return STATUS_BY_SLUG[tail] ? tail : null;
    }, [location.pathname]);

    const currentStatus = currentSlug ? STATUS_BY_SLUG[currentSlug] : null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                // Phase 1: instant list (name/address/status), no live trade lookups.
                const rows = await api.get('saved-traders?summary=true');
                if (cancelled) return;
                setSavedTraders(rows);
                setLoading(false);

                // Phase 2: fill each row's trade columns progressively. The server
                // rate-limits uktradeinfo, so a small client pool keeps it orderly;
                // rows update as their activity lands. The result also refreshes
                // name/address, self-healing any not-yet-snapshotted row.
                let next = 0;
                const worker = async () => {
                    while (!cancelled && next < rows.length) {
                        const { trader_id } = rows[next++];
                        try {
                            const a = await api.get(`saved-traders/${trader_id}/activity`);
                            if (cancelled) return;
                            setSavedTraders((prev) =>
                                prev.map((t) =>
                                    t.trader_id === trader_id
                                        ? { ...t, ...a, _activityLoaded: true }
                                        : t,
                                ),
                            );
                        } catch (e) {
                            if (cancelled) return;
                            setSavedTraders((prev) =>
                                prev.map((t) =>
                                    t.trader_id === trader_id
                                        ? { ...t, _activityLoaded: true }
                                        : t,
                                ),
                            );
                        }
                    }
                };
                await Promise.all(
                    Array.from({ length: Math.min(6, rows.length) }, worker),
                );
            } catch (e) {
                console.error('Error fetching saved traders:', e);
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const availableUsers = useMemo(() => {
        const map = new Map();
        savedTraders.forEach((t) => {
            if (t.user_id && !map.has(t.user_id)) {
                map.set(t.user_id, {
                    id: t.user_id,
                    name: fmtUser(t),
                });
            }
        });
        return Array.from(map.values());
    }, [savedTraders]);

    const stats = useMemo(() => {
        const total = savedTraders.length;
        const byStatus = {
            Prospect: 0,
            Client: 0,
            'Lapsed Client': 0,
            Rejected: 0,
        };
        savedTraders.forEach((t) => {
            if (byStatus[t.status] !== undefined) byStatus[t.status] += 1;
        });
        return { total, ...byStatus };
    }, [savedTraders]);

    const filtered = useMemo(() => {
        let list = savedTraders;
        if (currentStatus) list = list.filter((t) => t.status === currentStatus);
        if (userFilter !== 'all') list = list.filter((t) => t.user_id === userFilter);
        if (searchText.trim()) {
            const q = searchText.trim().toLowerCase();
            list = list.filter(
                (t) =>
                    t.company_name?.toLowerCase().includes(q) ||
                    t.address_1?.toLowerCase().includes(q) ||
                    t.post_code?.toLowerCase().includes(q),
            );
        }
        return list;
    }, [savedTraders, currentStatus, userFilter, searchText]);

    const handleStatusChange = async (traderId, newStatus) => {
        try {
            if (newStatus === 'Not Saved') {
                await api.delete(`saved-traders/${traderId}`);
                message.success('Trader removed from pipeline');
                setSavedTraders((prev) =>
                    prev.filter((t) => t.trader_id !== traderId),
                );
            } else {
                await api.put(`saved-traders/${traderId}/status`, {
                    status: newStatus,
                });
                message.success('Status updated');
                setSavedTraders((prev) =>
                    prev.map((t) =>
                        t.trader_id === traderId ? { ...t, status: newStatus } : t,
                    ),
                );
            }
        } catch (e) {
            console.error('Error updating status:', e);
            message.error('Failed to update trader status');
        }
    };

    // Paid plans only — the server answers 402 for trials and free orgs, and
    // the app-wide UpgradeModal takes it from there (message + link to plans).
    const handleDownload = async () => {
        hints.dismiss('saved-traders-download');
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            if (currentStatus) params.set('status', currentStatus);
            if (userFilter !== 'all') params.set('user_id', userFilter);
            if (searchText.trim()) params.set('q', searchText.trim());
            const qs = params.toString();
            const blob = await api.getBlob(`saved-traders/export${qs ? `?${qs}` : ''}`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `saved-traders-${dayjs().format('YYYY-MM-DD')}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            if (e.response?.status !== 402) {
                console.error('Error downloading saved traders:', e);
                message.error('Could not download your saved traders');
            }
        } finally {
            setDownloading(false);
        }
    };

    const countFor = (slug) => {
        if (!slug) return stats.total;
        return stats[STATUS_BY_SLUG[slug]] || 0;
    };

    const copy = PAGE_COPY[currentStatus ?? 'null'];

    const sidebar = (
        <>
            <div className="group">
                <span className="group-label">Pipeline</span>
                {FACETS.map((facet) => {
                    const path = facetPath(facet.slug);
                    const isOn = currentSlug === facet.slug;
                    return (
                        <button
                            key={facet.slug || 'all'}
                            type="button"
                            className={`item ${isOn ? 'on' : ''}`}
                            onClick={() => navigate(path)}
                        >
                            <span>
                                <span className={`dot ${facet.dot}`} />
                                {facet.label}
                            </span>
                            <span className="count">{countFor(facet.slug)}</span>
                        </button>
                    );
                })}
            </div>

            <div className="group">
                <span className="group-label">Filter</span>
                <div className="filter-group">
                    <div className="form-field">
                        <label htmlFor="filter-search">Search</label>
                        <input
                            id="filter-search"
                            type="search"
                            placeholder="Company, postcode…"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="filter-user">Saved by</label>
                        <select
                            id="filter-user"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                        >
                            <option value="all">Everyone</option>
                            {availableUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="spacer" />

            <div className="dash-side-foot">
                {filtered.length} of {stats.total} shown
            </div>
        </>
    );

    const renderRow = (t) => (
        <tr
            key={t.trader_id}
            className={`clickable status-row ${tintFor(t.status)}`}
            onClick={() => navigate(`/traders/${t.trader_id}`)}
        >
            <td className="co">
                {t.company_name || 'Unknown company'}
                <small>{fmtAddress(t)}</small>
            </td>
            <td className="num">
                {t._activityLoaded ? (
                    <>
                        {(t.import_peak_activity || 0)}+ entries
                        <span className="stack-2">{(t.import_count || 0)} months</span>
                    </>
                ) : (
                    <span className="stack-2">loading…</span>
                )}
            </td>
            <td className="num">
                {t._activityLoaded ? (
                    <>
                        {(t.export_peak_activity || 0)}+ entries
                        <span className="stack-2">{(t.export_count || 0)} months</span>
                    </>
                ) : (
                    <span className="stack-2">loading…</span>
                )}
            </td>
            {customsOn && (
                <td>
                    {t._activityLoaded ? <CustomsTags keys={t.customs_treatments} /> : <span className="stack-2">loading…</span>}
                </td>
            )}
            <td>
                {fmtUser(t)}
                <span className="stack-2">
                    {dayjs(t.created_at).format('DD MMM YYYY')}
                </span>
            </td>
            <td
                style={{ textAlign: 'right' }}
                onClick={(e) => e.stopPropagation()}
            >
                <StatusSelect
                    traderId={t.trader_id}
                    value={t.status}
                    onChange={handleStatusChange}
                />
            </td>
        </tr>
    );

    const renderCard = (t) => (
        <div
            key={t.trader_id}
            className={`trader-card status-row ${tintFor(t.status)}`}
            onClick={() => navigate(`/traders/${t.trader_id}`)}
        >
            <div className="co">{t.company_name || 'Unknown company'}</div>
            <div className="meta">{fmtAddress(t)}</div>
            <div className="trade-cell" style={{ marginTop: 12 }}>
                <div className="row">
                    <span className="lbl">Import</span>
                    <span className="val">
                        {t._activityLoaded
                            ? `${t.import_count || 0} mo · ${t.import_peak_activity || 0}+`
                            : 'loading…'}
                    </span>
                </div>
                <div className="row">
                    <span className="lbl">Export</span>
                    <span className="val">
                        {t._activityLoaded
                            ? `${t.export_count || 0} mo · ${t.export_peak_activity || 0}+`
                            : 'loading…'}
                    </span>
                </div>
                {customsOn && t._activityLoaded && t.customs_treatments?.length > 0 && (
                    <div className="row">
                        <span className="lbl">Might need</span>
                        <span className="val"><CustomsTags keys={t.customs_treatments} /></span>
                    </div>
                )}
            </div>
            <div className="row">
                <span className="saved-by">
                    {fmtUser(t)} · {dayjs(t.created_at).format('DD MMM')}
                </span>
                <span onClick={(e) => e.stopPropagation()}>
                    <StatusSelect
                        traderId={t.trader_id}
                        value={t.status}
                        onChange={handleStatusChange}
                        placement="left"
                    />
                </span>
            </div>
        </div>
    );

    return (
        <PageShell sidebar={sidebar}>
            <div className="page-head">
                <span className="kicker">Saved traders</span>
                <h1>{copy.title}</h1>
                <p>{copy.subtitle}</p>
            </div>

            <nav className="facet-pills" aria-label="Pipeline filter">
                {FACETS.map((facet) => (
                    <NavLink
                        key={facet.slug || 'all'}
                        to={facetPath(facet.slug)}
                        end={!facet.slug}
                        className={({ isActive }) =>
                            `facet-pill ${isActive ? 'on' : ''}`
                        }
                    >
                        <span className={`dot ${facet.dot}`} />
                        {facet.label}
                        <span className="count">{countFor(facet.slug)}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="section-head">
                <div>
                    <span className="kicker">{currentStatus || 'All stages'}</span>
                    <h2>
                        {filtered.length} trader{filtered.length === 1 ? '' : 's'}.
                    </h2>
                </div>
                <div className="section-actions">
                    {(searchText || userFilter !== 'all') && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px' }}
                            onClick={() => {
                                setSearchText('');
                                setUserFilter('all');
                            }}
                        >
                            Clear filters
                        </button>
                    )}
                    <Hint
                        id="saved-traders-download"
                        title="Download your list"
                        text="Get the traders shown here as a spreadsheet, ready for your CRM or to share with the team. It follows the stage and filters you've picked."
                        placement="bottom-end"
                        show={!loading && filtered.length > 0}
                    >
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px' }}
                            onClick={handleDownload}
                            disabled={downloading || loading || filtered.length === 0}
                        >
                            {downloading ? 'Downloading…' : 'Download CSV'}
                        </button>
                    </Hint>
                </div>
            </div>

            {loading ? (
                <Spinner block label="Loading pipeline" />
            ) : filtered.length === 0 ? (
                <div className="empty">
                    <span className="kicker">Nothing here</span>
                    <p>
                        {savedTraders.length === 0
                            ? 'No saved traders yet. Search and save accounts to build a pipeline.'
                            : 'No traders match the current filters.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="saved-table-wrap">
                        <table className="fs-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th style={{ width: 160 }}>Imports</th>
                                    <th style={{ width: 160 }}>Exports</th>
                                    {customsOn && <th style={{ width: 200 }}>Might need</th>}
                                    <th>Saved by</th>
                                    <th style={{ textAlign: 'right' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>{filtered.map(renderRow)}</tbody>
                        </table>
                    </div>

                    <div className="saved-cards">{filtered.map(renderCard)}</div>
                </>
            )}
        </PageShell>
    );
};

export default SavedTradersPage;
